/**
 * @file 学びメモ1件の編集欄 (題・紐づけとタグ・本文・画像・削除) である。
 *
 * @what: hooks/useStudyNoteEditor.ts の状態を受けて表示する。紐づけの並びには、まなぶ人の付け替え・
 *   付けた本・付けたタグ・画面ごとの追加のボタン (refExtras)・「＋ タグ」・「ほかの本」を置く。
 *   本文は Markdown の欄とプレビューを切り替え (広い画面では左右に並べても出せる)、画像は貼り付け・
 *   ドロップ・ボタンで足す。プレビューでは、言語が mermaid のコードの欄を図にし、図と画像を押すと
 *   画面いっぱいに拡大して見られる (NoteZoomOverlay)。
 * @why: 本を読む画面のメモと学びメモの画面で、同じメモを同じ形で書けるようにする。画面ごとに違うのは
 *   「いまのページ」「この本」のような文脈のボタンと、紐づけた本を開けるかだけである。
 *   タグの入力の置き場は代表が選んだ (紐づけと同じ行、2026-10-29)。
 * @failure: 描画のみ。保存と画像の失敗の文言は hook が持つ。
 */

import { useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { memberLabel, type FamilyMember } from "../api/members";
import type { StudyNoteRef } from "../api/notes";
import type { StudySource } from "../api/sources";
import { useMermaidBlocks } from "../hooks/useMermaidBlocks";
import type { NoteBodyView, StudyNoteEditorState } from "../hooks/useStudyNoteEditor";
import { renderMarkdown } from "../lib/markdown";
import { resolveNoteImageUrls } from "../model/noteImages";
import { toggledNoteRefs } from "../model/noteList";
import { addNoteTag, NOTE_TAG_MAX_LEN, NOTE_TAGS_MAX, removeNoteTag } from "../model/noteTags";
import { NoteZoomOverlay, type NoteZoomTarget } from "./NoteZoomOverlay";
import { StudyDialog } from "./StudyDialog";
import "./study.css";

const BODY_VIEWS: readonly { view: NoteBodyView; label: string }[] = [
  { view: "edit", label: "編集" },
  { view: "split", label: "並べる" },
  { view: "preview", label: "プレビュー" },
];

const pickImageFrom = (files: FileList | null | undefined): File | null =>
  Array.from(files ?? []).find((f) => f.type.startsWith("image/")) ?? null;

export function StudyNoteEditor({
  editor,
  learners,
  learnerPersonId,
  sources,
  refLabel,
  refHref,
  refExtras,
  tagSuggestions,
  allowSplit = false,
}: {
  editor: StudyNoteEditorState;
  /** 家族のまなぶ人 (2人以上のとき、メモの付け替えの選択肢に出す)。 */
  learners: readonly FamilyMember[];
  /** 画面で選んでいるまなぶ人である。 */
  learnerPersonId: string | null;
  /** 本だなの全資料 (「ほかの本」の候補)。 */
  sources: readonly StudySource[];
  /** 紐づけ先を人へ見せる1行にする。 */
  refLabel: (ref: StudyNoteRef) => string;
  /** 渡すと、紐づけた本の名前がその本を開くリンクになる。 */
  refHref?: (ref: StudyNoteRef) => string;
  /** 画面ごとの紐づけのボタンである。本を読む画面は「いまのページ」と「この本」を渡す。 */
  refExtras?: ReactNode;
  /** タグの候補 (そのまなぶ人の既存のタグ)。 */
  tagSuggestions: readonly string[];
  /** 編集欄とプレビューを左右に並べる出し方を選べるようにする (幅の広い学びメモの画面だけ)。 */
  allowSplit?: boolean;
}) {
  const {
    draft,
    selectedId,
    dirty,
    saving,
    savedAt,
    saveError,
    bodyView,
    setBodyView,
    confirmingDelete,
    setConfirmingDelete,
    removing,
    attaching,
    imageUrls,
    attachBody,
    fileInputRef,
    edit,
    onBodyInput,
    flushBody,
    save,
    draftToSave,
    removeNote,
    attachImage,
  } = editor;
  const [addSourceId, setAddSourceId] = useState("");
  // タグの入力欄である。「＋ タグ」を押すと開く。
  const [tagInputOpen, setTagInputOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const tagListId = useId();
  const previewRef = useRef<HTMLDivElement>(null);
  const [zoomTarget, setZoomTarget] = useState<NoteZoomTarget | null>(null);
  const view: NoteBodyView = bodyView === "split" && !allowSplit ? "edit" : bodyView;
  // renderMarkdown が DOMPurify で消毒してから返す (lib/markdown.ts)。
  // 画像参照 (note-image:<id>) は表示のときだけ blob URL に解決する。
  const previewHtml =
    draft === null || view === "edit"
      ? ""
      : renderMarkdown(resolveNoteImageUrls(draft.body, imageUrls));
  useMermaidBlocks(previewRef, previewHtml);

  if (draft === null) return null;

  const toggleRef = (target: StudyNoteRef) => edit({ refs: toggledNoteRefs(draft.refs, target) });
  const addableSources = sources.filter(
    (s) =>
      !draft.refs.some((r) => r.kind === "source" && r.source_id === s.id && (r.page ?? 0) === 0),
  );
  const candidateTags = tagSuggestions.filter((t) => !draft.tags.includes(t));

  const commitTag = () => {
    const next = addNoteTag(draft.tags, tagInput);
    if (next !== draft.tags) edit({ tags: [...next] });
    setTagInput("");
    setTagInputOpen(false);
  };

  return (
    <div className="study-note-editor">
      <input
        type="text"
        className="study-note-title-input"
        aria-label="ノートの名前"
        maxLength={120}
        value={draft.title}
        onChange={(e) => edit({ title: e.target.value })}
      />
      <div className="study-note-chips">
        {learners.length > 1 ? (
          <select
            className="study-note-learner-select"
            aria-label="だれのメモか"
            value={draft.learnerPersonId || (learnerPersonId ?? "")}
            onChange={(e) => edit({ learnerPersonId: e.target.value })}
          >
            {learners.map((l) => (
              <option key={l.person_id} value={l.person_id}>
                {memberLabel(l)}
              </option>
            ))}
          </select>
        ) : null}
        {draft.refs.map((ref, i) => (
          <span key={`${ref.source_id}-${ref.page ?? 0}-${i}`} className="study-note-chip">
            {refHref ? (
              <Link className="study-note-chip-link" to={refHref(ref)}>
                {refLabel(ref)}
              </Link>
            ) : (
              refLabel(ref)
            )}
            <button
              type="button"
              className="study-note-chip-x"
              aria-label={`${refLabel(ref)} への紐づけを外す`}
              onClick={() => toggleRef(ref)}
            >
              ×
            </button>
          </span>
        ))}
        {draft.tags.map((tag) => (
          <span key={`tag-${tag}`} className="study-note-chip study-note-chip--tag">
            #{tag}
            <button
              type="button"
              className="study-note-chip-x"
              aria-label={`タグ「${tag}」を外す`}
              onClick={() => edit({ tags: [...removeNoteTag(draft.tags, tag)] })}
            >
              ×
            </button>
          </span>
        ))}
        {refExtras}
        {draft.tags.length < NOTE_TAGS_MAX ? (
          tagInputOpen ? (
            <span className="study-note-tag-field">
              <input
                type="text"
                className="study-note-tag-input"
                aria-label="タグを足す"
                placeholder="タグ"
                maxLength={NOTE_TAG_MAX_LEN}
                list={tagListId}
                autoFocus
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  // 変換の確定の Enter では足さない
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    commitTag();
                  } else if (e.key === "Escape") {
                    setTagInput("");
                    setTagInputOpen(false);
                  }
                }}
                onBlur={commitTag}
              />
              <datalist id={tagListId}>
                {candidateTags.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </span>
          ) : (
            <button
              type="button"
              className="study-note-chip-add"
              onClick={() => setTagInputOpen(true)}
            >
              ＋ タグ
            </button>
          )
        ) : null}
        {addableSources.length > 0 ? (
          <span className="study-note-chip-addsource">
            <select
              aria-label="ほかの本への紐づけ"
              value={addSourceId}
              onChange={(e) => setAddSourceId(e.target.value)}
            >
              <option value="">ほかの本...</option>
              {addableSources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="study-note-btn"
              disabled={addSourceId === ""}
              onClick={() => {
                if (addSourceId === "") return;
                toggleRef({ kind: "source", source_id: addSourceId });
                setAddSourceId("");
              }}
            >
              足す
            </button>
          </span>
        ) : null}
      </div>
      <div className="study-note-body-bar" role="group" aria-label="本文の出し方">
        {BODY_VIEWS.filter((v) => allowSplit || v.view !== "split").map((v) => (
          <button
            key={v.view}
            type="button"
            className={`study-note-view-btn study-note-view-btn--${v.view}`}
            aria-pressed={view === v.view}
            onClick={() => {
              // プレビューは state の本文を出すので、移る前に打ちかけを送る。
              flushBody();
              setBodyView(v.view);
            }}
          >
            {v.label}
          </button>
        ))}
      </div>
      {/* 入力欄を先頭に置き続けて、編集と「並べる」の切り替えで欄を作り直さない (カーソルを保つ) */}
      <div className={`study-note-body-area is-${view}`}>
        {view !== "preview" ? (
          <textarea
            // ノートを替えたら作り直し、そこで手元の本文を入れ直す (React は値を書かない)。
            key={selectedId ?? ""}
            ref={attachBody}
            className="study-note-body"
            aria-label="学びメモ (Markdown)"
            placeholder={
              "## 見出し\n\n- 学んだこと\n- つまずいたこと\n\n画像は貼り付け・ドロップでも入ります"
            }
            onChange={(e) => onBodyInput(e.target.value)}
            onBlur={() => {
              flushBody();
              if (dirty && selectedId !== null) {
                void save(selectedId, draftToSave(draft));
              }
            }}
            onPaste={(e) => {
              const file = pickImageFrom(e.clipboardData?.files);
              if (file) {
                e.preventDefault();
                void attachImage(file);
              }
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const file = pickImageFrom(e.dataTransfer?.files);
              if (file) {
                e.preventDefault();
                void attachImage(file);
              }
            }}
          />
        ) : null}
        {view !== "edit" ? (
          <div
            ref={previewRef}
            className="study-note-preview"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
            onClick={(e) => {
              const el = e.target as Element;
              // リンクの中の画像は、リンクとして開く
              if (el.closest("a")) return;
              const diagram = el.closest(".study-note-mermaid");
              if (diagram) {
                setZoomTarget({ kind: "diagram", svg: diagram.innerHTML });
                return;
              }
              if (el instanceof HTMLImageElement) {
                setZoomTarget({ kind: "image", src: el.src, alt: el.alt });
              }
            }}
          />
        ) : null}
      </div>
      <div className="study-note-foot">
        <span className="study-note-foot-left">
          {view !== "preview" ? (
            <label className="study-note-image-field">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = pickImageFrom(e.target.files);
                  if (file) void attachImage(file);
                }}
              />
              <span className="study-note-btn" role="button">
                {attaching ? "画像を追加しています..." : "画像を追加する"}
              </span>
            </label>
          ) : null}
        </span>
        <span className="study-note-save-state" role="status">
          {saveError ??
            (saving
              ? "保存しています..."
              : dirty
                ? "変更あり"
                : savedAt
                  ? `保存済み ${savedAt}`
                  : "")}
        </span>
      </div>
      {/* 削除は誤タップしにくいよう、操作ボタンの並びから離した控えめなリンクにする */}
      <div className="study-note-delete-row">
        <button
          type="button"
          className="study-note-delete-link"
          onClick={() => setConfirmingDelete(true)}
        >
          ページを削除する
        </button>
      </div>
      {zoomTarget ? (
        <NoteZoomOverlay target={zoomTarget} onClose={() => setZoomTarget(null)} />
      ) : null}
      {confirmingDelete ? (
        <StudyDialog
          title="ページを削除しますか"
          labelledBy="study-note-delete-title"
          onClose={() => setConfirmingDelete(false)}
        >
          <p>「{draft.title}」を削除します。添付した画像も一緒に消えて、もどせません。</p>
          <div className="study-note-delete-actions">
            <button
              type="button"
              className="study-note-btn"
              disabled={removing}
              onClick={() => setConfirmingDelete(false)}
            >
              やめる
            </button>
            <button
              type="button"
              className="study-note-btn study-note-btn--danger"
              disabled={removing}
              onClick={() => void removeNote()}
            >
              {removing ? "削除しています..." : "削除する"}
            </button>
          </div>
        </StudyDialog>
      ) : null}
    </div>
  );
}
